import React, { ReactNode, useEffect, useState, useRef } from 'react';
import DropdownButtonForSearch from '../../components/Dropdowns/DropdownButtonForSearch';
import CheckBoxWithLabel from '../../components/checkbox/CheckBoxWithLabel';
import ButtonWithImg from '../../components/buttons/ButtonWithImg';
import DynamicFilterPanel, { DynamicFilterConfig } from '../../components/DynamicComponent/DynamicFilterPanel';
import useApi from '../../services/useApi';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { AppDispatch } from '../../store';
import { showToast } from '../../components/toastMessage/toastSlice';
import useTranslation from '../../components/CustomHooks/useTranslation';
import { breadcrumbsContent } from '../../components/breadcrumbs/BreadcrumbsContent';

interface RenderExtraHeadingsProps {
    type: string;
    entireResponse: any;
    loading: boolean;
    // Account Statement Report props
    userOptions?: string[];
    selectedUser?: string;
    dropdownLoading?: boolean;
    handleUserSelection?: (id: string, value: string) => void;
    loadMoreUsers?: (searchValue: string, isPageNumberChange?: boolean) => Promise<void>;
    handleUserSearch?: (searchValue: string) => void;
    handleTransactionSearch?: (searchValue: string) => void;
    selectedUserData?: any;
    activeTab?: 'stock' | 'money';
    hasStockAccountPrivilege?: boolean;
    hasMoneyAccountPrivilege?: boolean;
    handleTabSwitch?: (tab: 'stock' | 'money') => void;
    // Filter and Export props for Account Statement Report
    dynamicFilterConfig?: DynamicFilterConfig;
    onFilter?: (filters: { [key: string]: string | boolean | null } | null) => void;
    searchParams?: URLSearchParams;
    exportEndpoint?: string;
    disableExport?: boolean;
    recordCount?: number;
    // MNO Wise eTop Up Purchase Report props
    mnoOptions?: Array<{ id: string; name: string }>;
    selectedMnos?: string[];
    handleMnoCheckbox?: (mnoName: string, checked: boolean) => void;
    setFetchListTrigger?: React.Dispatch<React.SetStateAction<boolean>>;
    // Merchant / Customer eTop Up Wallet Balance Report props
    walletBalanceActiveTab?: 'etopup-stocks' | 'digital-wallet';
    handleWalletBalanceTabSwitch?: (tab: 'etopup-stocks' | 'digital-wallet') => void;
}

export const renderExtraHeadingsByType = (props: RenderExtraHeadingsProps): ReactNode => {
    const { type } = props;

    switch (type) {
        case 'Account Statement Report':
            return renderAccountStatementHeading(props);

        case 'MNO Wise eTop Up Purchase Report':
            return renderMnoWiseEtopupPurchaseHeading(props);

        case 'MNO Wise eTop Up Available Balance Report':
            return renderMnoWiseEtopupAvailableBalanceHeading();

        case "MNO's Service Fee Earned Report":
            return <MnoServiceFeeEarnedTopBar setFetchListTrigger={props.setFetchListTrigger || (() => { })} />;

        case "Merchant / Customer eTop Up Wallet Balance Report":
            return renderWalletBalanceHeading(props);

        case "MNO Wise Sales Trend Report":
            return renderMnoWiseSalesTrendHeading();

        case "Stock Transfer Report":
        case "Upfront Sales Report":
            return renderChannelCountsHeading(props);

        default:
            return null;
    }
};

const AccountStatementHeading: React.FC<RenderExtraHeadingsProps> = (props) => {
    const {
        userOptions = [],
        selectedUser,
        dropdownLoading = false,
        handleUserSelection,
        loadMoreUsers,
        handleUserSearch,
        handleTransactionSearch,
        selectedUserData,
        activeTab = 'stock',
        hasStockAccountPrivilege = false,
        hasMoneyAccountPrivilege = false,
        handleTabSwitch,
        dynamicFilterConfig,
        onFilter,
        searchParams,
        exportEndpoint,
        disableExport = false,
        loading = false,
        recordCount = 0
    } = props;

    const [isExportOpen, setIsExportOpen] = useState(false);
    const exportRef = useRef<HTMLDivElement>(null);
    const dispatch = useDispatch<AppDispatch>();
    const { translate, notEnglish } = useTranslation(breadcrumbsContent);
    const { getAPI } = useApi();
    const [exportLoading, setExportLoading] = useState(false);

    const handleClickOutside = (event: MouseEvent) => {
        if (
            exportRef.current &&
            !exportRef.current.contains(event.target as Node)
        ) {
            setIsExportOpen(false);
        }
    };

    useEffect(() => {
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    const exportOptions = [
        { label: "CSV", value: "csv", textColorClass: "text-positive" },
        { label: "PDF", value: "pdf", textColorClass: "text-s_dark_hover" },
        { label: "EXCEL", value: "xlsx", textColorClass: "text-positive" },
    ];

    // Get date filter text for Statement of Accounts
    const getDateFilterText = () => {
        if (!dynamicFilterConfig || !searchParams) return null;

        const dateRangeConfig = Object.values(dynamicFilterConfig).find(
            config => config.type === "date" && typeof config.searchKey === "object"
        );

        if (!dateRangeConfig || typeof dateRangeConfig.searchKey !== "object") return null;

        const startDate = searchParams.get(dateRangeConfig.searchKey.start);
        const endDate = searchParams.get(dateRangeConfig.searchKey.end);

        if (!startDate || !endDate) return null;

        const formatDate = (dateStr: string) => {
            const date = new Date(dateStr);
            return date.toLocaleDateString('en-GB', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric'
            }).replace(/\//g, '-');
        };

        const formattedStartDate = formatDate(startDate);
        const formattedEndDate = formatDate(endDate);

        return `${formattedStartDate} to ${formattedEndDate}`;
    };

    const dateFilterText = getDateFilterText();
    
    // Show buttons only when user is selected and there are records
    const shouldShowButtons = selectedUserData && recordCount > 0;

    const handleExportOptionClick = async (value: string): Promise<void> => {
        setIsExportOpen(false);
        try {
            setExportLoading(true);
            const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
            const res = await getAPI(`${exportEndpoint}&export_format=${value}&client_timezone=${timezone}`);
            if (!res.error) {
                if (res.data.url) {
                    window.open(res.data.url, "_blank");
                    dispatch(
                        showToast({
                            type: "success",
                            message: translate("List exported successfully"),
                        })
                    );
                } else {
                    dispatch(
                        showToast({
                            type: "success",
                            message: res.data.message || translate(`Export in progress. You'll receive an email with the ${value.toLocaleUpperCase()} once it's ready.`),
                        })
                    );
                }
            } else {
                dispatch(
                    showToast({
                        type: "error",
                        message: "something went wrong",
                    })
                );
            }
        } catch {
            dispatch(
                showToast({
                    type: "error",
                    message: "something went wrong",
                })
            );
        } finally {
            setExportLoading(false);
        }
    };

    return (
        <>
            {/* User dropdown, filter, and export buttons in the same row */}
            <div className="flex items-end justify-between w-full mt-3 px-4">
                <div className="flex-1 max-w-[320px]">
                    <DropdownButtonForSearch
                        options={userOptions}
                        labelName={'Select a user'}
                        editLoading={false}
                        id={'account_statement_report_dropdown'}
                        tabIndex={0}
                        noSearch={false}
                        onChange={handleUserSelection || (() => { })}
                        selectedValue={selectedUser}
                        required={false}
                        errorMessage={''}
                        disabled={false}
                        modalStyle="pr-4 w-[336px]"
                        width="w-[320px]"
                        height="h-10 mb-2 focus:mb-[8px] focus:py-[20.1px] focus:pl-[10.2px]"
                        dropdownTopClass="top-[70px]"
                        toolTipClassName={'top-0 left-5'}
                        dynamicDropdown={dropdownLoading}
                        loading={dropdownLoading}
                        loadMoreOptions={async (searchValue: string, isPageNumberChange?: boolean) => {
                            if (isPageNumberChange && loadMoreUsers) {
                                await loadMoreUsers(searchValue, isPageNumberChange);
                            } else if (handleUserSearch) {
                                handleUserSearch(searchValue);
                            }
                        }}
                        handleSearchValue={(searchValue: string) => {
                            if (handleUserSearch) {
                                handleUserSearch(searchValue);
                            }
                            if (selectedUserData && handleTransactionSearch) {
                                handleTransactionSearch(searchValue);
                            }
                        }}
                        testId={'account_statement_dropdown'}
                        dropdownTestId={'account_statement_dropdown_list'}
                        placeholder={'a user'}
                    />
                </div>
                
                {/* Statement of Accounts text and Filter/Export buttons */}
                <div className="flex items-center gap-3 mb-2">
                    {/* Statement of Accounts text */}
                    {dateFilterText && (
                        <div className="text-sm w-[196px] text-center text-black font-medium flex flex-col">
                            <span>Statement of Accounts</span>
                            <span>{dateFilterText}</span>
                        </div>
                    )}
                    
                    {/* Filter button */}
                    {shouldShowButtons && dynamicFilterConfig && onFilter && searchParams && (
                        <DynamicFilterPanel
                            config={dynamicFilterConfig}
                            onApply={onFilter}
                            onClear={() => onFilter(null)}
                            initialValues={{}}
                            className=""
                            customHeight="h-[101px] md:h-[103px]"
                            searchParams={searchParams}
                            balanceSheet={false}
                            ticketList={false}
                        />
                    )}
                    
                    {/* Export button */}
                    {shouldShowButtons && exportEndpoint && (
                        <div className="relative" ref={exportRef}>
                            <ButtonWithImg
                                buttonName={translate("Export")}
                                disabledButton={loading || disableExport}
                                ImgSrc={loading || disableExport ? "loading-export" : "export"}
                                testId="export"
                                className={`px-4 bg-p_primary text-white ${notEnglish ? "w-fit" : "w-[110px]"}`}
                                buttonOnclick={() => setIsExportOpen(!isExportOpen)}
                                loading={exportLoading}
                            />
                            {isExportOpen && (
                                <div className="absolute right-0 mt-1 w-[110px] bg-white border border-s_dark_hover rounded-lg z-[70]">
                                    <ul className="flex flex-col">
                                        {exportOptions.map((option) => (
                                            <li
                                                key={option.value}
                                                data-testid={`${option.value}`}
                                                className={`p-2 text-body2 cursor-pointer ${option.textColorClass} hover:bg-p_light rounded-lg`}
                                                onClick={() => handleExportOptionClick(option.value)}
                                            >
                                                {option.label}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
            
            {/* Tab switches */}
            <div className="flex flex-row gap-6 pt-3 pb-2 pl-4">
                <div
                    className={`text-subTitle ${activeTab === 'stock'
                        ? 'text-p_primary border-b-2 border-p_primary'
                        : hasStockAccountPrivilege
                            ? 'text-gray-400 border-b-2 border-gray-400'
                            : 'text-gray-300 border-b-2 border-gray-300'
                        } ${hasStockAccountPrivilege
                            ? 'cursor-pointer'
                            : 'cursor-not-allowed opacity-50'
                        }`}
                    onClick={() => hasStockAccountPrivilege && handleTabSwitch && handleTabSwitch('stock')}
                >
                    Stock Account Statement
                </div>
                <div
                    className={`text-subTitle ${activeTab === 'money'
                        ? 'text-p_primary border-b-2 border-p_primary'
                        : hasMoneyAccountPrivilege
                            ? 'text-gray-400 border-b-2 border-gray-400'
                            : 'text-gray-300 border-b-2 border-gray-300'
                        } ${hasMoneyAccountPrivilege
                            ? 'cursor-pointer'
                            : 'cursor-not-allowed opacity-50'
                        }`}
                    onClick={() => hasMoneyAccountPrivilege && handleTabSwitch && handleTabSwitch('money')}
                >
                    Money Account Statement
                </div>
            </div>
        </>
    );
};

const renderAccountStatementHeading = (props: RenderExtraHeadingsProps): ReactNode => {
    return <AccountStatementHeading {...props} />;
};

const renderMnoWiseEtopupPurchaseHeading = (props: RenderExtraHeadingsProps): ReactNode => {
    const { entireResponse } = props;

    if (!entireResponse?.mno_summary) return null;

    return (
        <div className="mt-4 px-4">
            <div className="rounded-lg">
                <h3 className="text-lg font-semibold mb-3 text-gray-800">
                    Total eTop Up Purchase Summary
                </h3>
                <div className="flex overflow-auto scrollHide pb-2 gap-4">
                    {entireResponse?.mno_summary.map((mno: any, index: number) => (
                        <div
                            key={index}
                            className="bg-[#E7EDF5] rounded-md p-3 border border-gray-200 w-[292px] flex-shrink-0"
                        >
                            <div className="text-body2 text-gray-500">
                                Transactions of {mno.mno_name} -{' '}
                                <span className="font-semibold text-gray-700">
                                    {mno.total_transactions}
                                </span>
                            </div>
                            <div className="text-body2 text-gray-500">
                                Purchase of {mno.mno_name} -{' '}
                                <span className="font-semibold text-gray-700">
                                    AFA {mno.total_amount.toFixed(2)}
                                </span>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

const renderMnoWiseEtopupAvailableBalanceHeading = (): ReactNode => {
    // Add specific logic for MNO Wise eTop Up Available Balance Report if needed
    return null;
};

const MnoServiceFeeEarnedTopBar: React.FC<{ setFetchListTrigger: React.Dispatch<React.SetStateAction<boolean>> }> = ({ setFetchListTrigger }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams] = useSearchParams();
    const { getAPI } = useApi();
    const [mnoOptions, setMnoOptions] = useState<any[]>([]);
    const [selectedMnos, setSelectedMnos] = useState<string[]>(['All']);

    const fetchMnos = async () => {
        try {
            const response = await getAPI('reports/mno-available-balance');
            setMnoOptions(response.data.data.map((mno: any) => ({
                id: mno.mno_id,
                name: mno.mno_name
            })));
        } catch {
            setMnoOptions([]);
        }
    };

    const handleMnoCheckbox = (mnoName: string, checked: boolean) => {
        let newSelectedMnos: string[] = [];

        if (mnoName === 'All') {
            newSelectedMnos = checked ? ['All'] : [];
        } else {
            const selectedMno = mnoOptions.find(mno => mno.name === mnoName);
            const mnoId = selectedMno?.id;
            if (mnoId) {
                if (checked) {
                    // Adding an MNO
                    if (selectedMnos.includes('All')) {
                        // If All was selected, keep all MNOs selected
                        newSelectedMnos = mnoOptions.map(mno => mno.id);
                    } else {
                        // Add to current selection
                        newSelectedMnos = [...selectedMnos, mnoId];
                        // Check if all MNOs are now selected
                        if (newSelectedMnos.length === mnoOptions.length) {
                            newSelectedMnos = ['All'];
                        }
                    }
                } else {
                    // Removing an MNO
                    if (selectedMnos.includes('All')) {
                        // If All was selected, select all except this one
                        newSelectedMnos = mnoOptions.map(mno => mno.id).filter(id => id !== mnoId);
                    } else {
                        // Remove from current selection
                        newSelectedMnos = selectedMnos.filter(id => id !== mnoId);
                    }
                }
            }
        }

        setSelectedMnos(newSelectedMnos);

        // Update URL params
        const currentParams = new URLSearchParams(location.search);
        if (newSelectedMnos.includes('All') || newSelectedMnos.length === 0) {
            currentParams.delete('mno_id');
        } else {
            currentParams.set('mno_id', newSelectedMnos.join(','));
        }
        currentParams.set('page_number', '1');
        navigate(`${location.pathname}?${currentParams.toString()}`, { replace: true });

        setFetchListTrigger(prev => !prev);
    };

    // Initialize selectedMnos from URL parameters
    useEffect(() => {
        const mnoIdParam = searchParams.get('mno_id');
        if (mnoIdParam) {
            const mnoIds = mnoIdParam.split(',');
            setSelectedMnos(mnoIds);
        }
    }, [searchParams]);

    useEffect(() => {
        fetchMnos();
    }, []);

    return (
        <div className="flex flex-wrap gap-4 pt-4 px-[13px] rounded-lg">
            {mnoOptions.length > 0 && (
                <CheckBoxWithLabel
                    label="All"
                    checked={selectedMnos.includes('All')}
                    handelCheckBox={handleMnoCheckbox}
                />
            )}

            {mnoOptions.map((mno) => (
                <CheckBoxWithLabel
                    key={mno.id}
                    label={mno.name}
                    checked={selectedMnos.includes('All') || selectedMnos.includes(mno.id)}
                    handelCheckBox={handleMnoCheckbox}
                />
            ))}
        </div>
    );
};

const renderMnoWiseSalesTrendHeading = (): ReactNode => {
    return (
        <div className="mt-2 mx-0.5">
            <div className="flex">
                <button
                    className="mr-3 ml-6 pt-2 pb-0 text-subTitle font-medium border-b-2 transition-colors text-p_primary border-p_primary"
                >
                    eTop Up Sales Trend Report
                </button>
                <button
                    disabled={true}
                    className="ml-3 mr-6 pt-2 pb-0 text-subTitle font-medium border-b-2 transition-colors border-secondary_text text-gray-400 cursor-not-allowed"
                >
                    Bundle Activation Sales Trend Report
                </button>
            </div>
        </div>
    );
};

const renderChannelCountsHeading = (props: RenderExtraHeadingsProps): ReactNode => {
    const { entireResponse } = props;
    const channelCounts = entireResponse?.channel_counts;

    if (!channelCounts) return null;

    const { as_of: asOf, Mobile = 0, Web = 0, USSD = 0 } = channelCounts;

    const formatAsOfDate = (dateStr?: string): string => {
        if (!dateStr) return '';
        const date = new Date(dateStr);
        if (isNaN(date.getTime())) return '';
        return date.toLocaleDateString('en-GB', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
        }).replace(/\//g, '-');
    };

    const cards = [
        { label: 'Mobile', value: Mobile },
        { label: 'Web', value: Web },
        { label: 'USSD', value: USSD },
    ];

    const asOfText = formatAsOfDate(asOf);

    return (
        <div className="bg-white pt-4 px-4">
            {asOfText && (
                <div className="text-body2 text-gray-500 mb-2">
                    The Data Shown as of {asOfText} (11:59pm Kabul time)
                </div>
            )}
            <div className="flex pb-2 gap-4">
                {cards.map((card) => (
                    <div
                        key={card.label}
                        className="bg-[#E7EDF5] rounded-2xl p-3 border border-gray-200 flex-1 min-w-[200px]"
                    >
                        <div className="flex flex-col gap-1 justify-center items-center">
                            <span className="text-body2 text-center text-black">{card.label}</span>
                            <span className="font-bold text-gray-700">
                                {Number(card.value ?? 0).toLocaleString('en-US')}
                            </span>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

const renderWalletBalanceHeading = (props: RenderExtraHeadingsProps): ReactNode => {
    const { walletBalanceActiveTab = 'etopup-stocks', handleWalletBalanceTabSwitch } = props;

    return (
        <div className="mt-2 mx-0.5">
            <div className="flex">
                <button
                    onClick={() => handleWalletBalanceTabSwitch?.('etopup-stocks')}
                    className={`mr-3 ml-6 py-2 text-subTitle font-medium border-b-2 transition-colors ${
                        walletBalanceActiveTab === 'etopup-stocks'
                            ? 'text-p_primary border-b-2 border-p_primary'
                            : 'border-transparent text-gray-500 hover:text-gray-700'
                    }`}
                >
                    eTop Up Stocks
                </button>
                <button
                    onClick={() => handleWalletBalanceTabSwitch?.('digital-wallet')}
                    disabled={true}
                    className={`ml-3 mr-6 py-2 text-subTitle font-medium border-b-2 transition-colors ${
                        walletBalanceActiveTab === 'digital-wallet'
                            ? 'text-p_primary border-b-2 border-p_primary'
                            : 'border-secondary_text text-gray-400 cursor-not-allowed'
                    }`}
                >
                    Digital Wallet
                </button>
            </div>
        </div>
    );
};